import { useMemo, useState } from "react";
import { Link, useMatchRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, BASE_PATH } from "@/api/client";
import type { ChannelLiveState } from "@/hooks/useChannelLiveState";
import { useAgentOrder } from "@/hooks/useAgentOrder";
import { Button } from "@/ui";
import { ArrowLeft01Icon, DashboardSquare01Icon, Settings01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { CreateAgentDialog } from "@/components/CreateAgentDialog";

interface SidebarProps {
	liveStates: Record<string, ChannelLiveState>;
	collapsed: boolean;
	onToggle: () => void;
}

interface SortableAgentItemProps {
	agentId: string;
	displayName?: string;
	activity?: { workers: number; branches: number };
	isActive: boolean;
	collapsed: boolean;
}

function SortableAgentItem({ agentId, displayName, activity, isActive, collapsed }: SortableAgentItemProps) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: agentId });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
		cursor: isDragging ? 'grabbing' : 'grab',
	};

	if (collapsed) {
		return (
			<div ref={setNodeRef} style={style} {...attributes} {...listeners}>
				<Link
					to="/agents/$agentId"
					params={{ agentId }}
					className={`flex h-8 w-8 items-center justify-center rounded-md text-xs font-medium ${
						isActive ? "bg-sidebar-selected text-sidebar-ink" : "text-sidebar-inkDull hover:bg-sidebar-selected/50"
					}`}
					style={{ pointerEvents: isDragging ? 'none' : 'auto' }}
					title={displayName ?? agentId}
				>
					{(displayName ?? agentId).charAt(0).toUpperCase()}
				</Link>
			</div>
		);
	}

	return (
		<div ref={setNodeRef} style={style} className="mx-2" {...attributes} {...listeners}>
			<Link
				to="/agents/$agentId"
				params={{ agentId }}
				className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm ${
					isActive
						? "bg-sidebar-selected text-sidebar-ink"
						: "text-sidebar-inkDull hover:bg-sidebar-selected/50"
				}`}
				style={{ pointerEvents: isDragging ? 'none' : 'auto' }}
			>
				<span className="flex-1 truncate">{displayName ?? agentId}</span>
				{activity && (activity.workers > 0 || activity.branches > 0) && (
					<div className="flex items-center gap-1">
						{activity.workers > 0 && (
							<span className="rounded bg-amber-500/15 px-1 py-0.5 text-tiny text-amber-400">
								{activity.workers}w
							</span>
						)}
						{activity.branches > 0 && (
							<span className="rounded bg-violet-500/15 px-1 py-0.5 text-tiny text-violet-400">
								{activity.branches}b
							</span>
						)}
					</div>
				)}
			</Link>
		</div>
	);
}

export function Sidebar({ liveStates, collapsed, onToggle }: SidebarProps) {
	const [createOpen, setCreateOpen] = useState(false);

	const { data: agentsData } = useQuery({
		queryKey: ["agents"],
		queryFn: api.agents,
		refetchInterval: 30_000,
	});

	const { data: channelsData } = useQuery({
		queryKey: ["channels"],
		queryFn: api.channels,
		refetchInterval: 10_000,
	});

	const agents = agentsData?.agents ?? [];
	const channels = channelsData?.channels ?? [];
	
	const agentIds = useMemo(() => agents.map((a) => a.id), [agents]);
	const agentDisplayNames = useMemo(() => {
		const map: Record<string, string | undefined> = {};
		for (const a of agents) map[a.id] = a.display_name;
		return map;
	}, [agents]);
	const [agentOrder, setAgentOrder] = useAgentOrder(agentIds);

	const matchRoute = useMatchRoute();
	const isOverview = matchRoute({ to: "/" });
	const isSettings = matchRoute({ to: "/settings" });

	const agentActivity = useMemo(() => {
		const byAgent: Record<string, { workers: number; branches: number }> = {};
		for (const channel of channels) {
			const live = liveStates[channel.id];
			if (!live) continue;
			if (!byAgent[channel.agent_id]) byAgent[channel.agent_id] = { workers: 0, branches: 0 };
			byAgent[channel.agent_id].workers += Object.keys(live.workers).length;
			byAgent[channel.agent_id].branches += Object.keys(live.branches).length;
		}
		return byAgent;
	}, [channels, liveStates]);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				delay: 150,
				tolerance: 5,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (over && active.id !== over.id) {
			const oldIndex = agentOrder.indexOf(active.id as string);
			const newIndex = agentOrder.indexOf(over.id as string);
			setAgentOrder(arrayMove(agentOrder, oldIndex, newIndex));
		}
	};

	return (
		<motion.nav
			className="flex h-full flex-col overflow-hidden border-r border-sidebar-line bg-sidebar"
			animate={{ width: collapsed ? 56 : 224 }}
			transition={{ type: "spring", stiffness: 500, damping: 35 }}
		>
			{/* Logo + collapse toggle */}
			<div className="flex h-12 items-center border-b border-sidebar-line px-3">
			{collapsed ? (
				<button onClick={onToggle} className="flex h-full w-full items-center justify-center">
					<img src={`${BASE_PATH}/ball.png`} alt="" className="h-6 w-6 transition-transform duration-150 ease-out hover:scale-110 active:scale-95" draggable={false} />
				</button>
			) : (
				<div className="flex flex-1 items-center justify-between">
					<Link to="/" className="flex items-center gap-2">
						<img src={`${BASE_PATH}/ball.png`} alt="" className="h-6 w-6 flex-shrink-0 transition-transform duration-150 ease-out hover:scale-110 active:scale-95" draggable={false} />
						<span className="whitespace-nowrap font-plex text-sm font-semibold text-sidebar-ink">
							Spacebot
						</span>
					</Link>
					<Button
						onClick={onToggle}
						variant="ghost"
						size="icon"
						className="h-6 w-6 text-sidebar-inkFaint hover:bg-sidebar-selected/50 hover:text-sidebar-inkDull"
					>
						<HugeiconsIcon icon={ArrowLeft01Icon} className="h-4 w-4" />
					</Button>
				</div>
			)}
			</div>

			{/* Collapsed: icon-only nav */}
			{collapsed ? (
				<div className="flex flex-col items-center gap-1 pt-2">
				<Link
					to="/"
					className={`flex h-8 w-8 items-center justify-center rounded-md ${
						isOverview ? "bg-sidebar-selected text-sidebar-ink" : "text-sidebar-inkDull hover:bg-sidebar-selected/50"
					}`}
					title="Dashboard"
				>
					<HugeiconsIcon icon={DashboardSquare01Icon} className="h-4 w-4" />
				</Link>
				<Link
					to="/settings"
					className={`flex h-8 w-8 items-center justify-center rounded-md ${
						isSettings ? "bg-sidebar-selected text-sidebar-ink" : "text-sidebar-inkDull hover:bg-sidebar-selected/50"
					}`}
					title="Settings"
				>
					<HugeiconsIcon icon={Settings01Icon} className="h-4 w-4" />
				</Link>
				<div className="my-1 h-px w-5 bg-sidebar-line" />
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}
				>
					<SortableContext items={agentOrder} strategy={verticalListSortingStrategy}>
					{agentOrder.map((agentId) => {
						const isActive = !!matchRoute({ to: "/agents/$agentId", params: { agentId }, fuzzy: true });
						return (
							<SortableAgentItem
								key={agentId}
								agentId={agentId}
								displayName={agentDisplayNames[agentId]}
								isActive={isActive}
								collapsed={true}
							/>
						);
					})}
					</SortableContext>
				</DndContext>
				<button
					onClick={() => setCreateOpen(true)}
					className="flex h-8 w-8 items-center justify-center rounded-md text-sidebar-inkFaint hover:bg-sidebar-selected/50 hover:text-sidebar-inkDull"
					title="New Agent"
				>
					+
				</button>
				</div>
			) : (
				<>
					{/* Top-level nav */}
					<div className="flex flex-col gap-0.5 pt-2">
						<Link
							to="/"
							className={`mx-2 flex items-center gap-2 rounded-md px-2 py-1.5 text-sm ${
								isOverview
									? "bg-sidebar-selected text-sidebar-ink"
									: "text-sidebar-inkDull hover:bg-sidebar-selected/50"
							}`}
						>
							Dashboard
						</Link>
					<Link
						to="/settings"
						className={`mx-2 flex items-center gap-2 rounded-md px-2 py-1.5 text-sm ${
							isSettings
								? "bg-sidebar-selected text-sidebar-ink"
								: "text-sidebar-inkDull hover:bg-sidebar-selected/50"
						}`}
					>
						Settings
					</Link>
				</div>

					{/* Agents */}
					<div className="flex flex-1 flex-col overflow-y-auto pt-3">
						<span className="px-3 pb-1 text-tiny font-medium uppercase tracking-wider text-sidebar-inkFaint">
							Agents
						</span>
						{agents.length === 0 ? (
							<span className="px-3 py-2 text-tiny text-sidebar-inkFaint">
								No agents configured
							</span>
						) : (
							<DndContext
								sensors={sensors}
								collisionDetection={closestCenter}
								onDragEnd={handleDragEnd}
							>
								<SortableContext items={agentOrder} strategy={verticalListSortingStrategy}>
									<div className="flex flex-col gap-0.5">
									{agentOrder.map((agentId) => {
										const activity = agentActivity[agentId];
										const isActive = !!matchRoute({ to: "/agents/$agentId", params: { agentId }, fuzzy: true });

										return (
											<SortableAgentItem
												key={agentId}
												agentId={agentId}
												displayName={agentDisplayNames[agentId]}
												activity={activity}
												isActive={isActive}
												collapsed={false}
											/>
										);
									})}
									</div>
								</SortableContext>
							</DndContext>
						)}
					<Button
						variant="outline"
						size="sm"
						onClick={() => setCreateOpen(true)}
						className="mx-2 mt-1 w-auto justify-center border-dashed border-sidebar-line text-sidebar-inkFaint hover:border-sidebar-inkFaint hover:text-sidebar-inkDull"
					>
						+ New Agent
					</Button>
					</div>
				</>
			)}
			<CreateAgentDialog open={createOpen} onOpenChange={setCreateOpen} />
		</motion.nav>
	);
}
